import React, { useEffect, useState } from "react";
import { Container, Row, Col, Button, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import styles from "../../../../modules/Library/AdverseEvent/css/adverseEvent.module.scss";
import ip from "../../IPinventory/css/ipInventory.module.scss";
import { DISPOSAL_PATHS } from "../../../../Routes/paths";
import {
  formatDate,
  GetUserData,
  showToastMessage,
} from "../../../../common/service/CommonService";
import CommonTableIndexWise from "../../../../Components/Common/CommonTableIndexWise";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import { useDisposalService } from "../api/useDisposal";
import { queryClient } from "../../../../api/queryClient";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "../../../../common/constants/PermissionConstants";
import CommonButton from "../../../../Components/Common/CommonButton";
import PinVerificationModal from "../../DailyReconsilation/components/PinVerificationModal";
import StatCard from "../../../../Components/Common/StatCard";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useGlobalStudy } from "context/StudyContext";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import NoStudySelectedPlaceholder from "../../../../Components/Common/NoStudySelectedPlaceholder";

interface DisposalRecord {
  disposalid: number;
  disposalnumber: string;
  studycode: string;
  studyid: number;
  productname: string;
  batchlotno: string;
  ipcategory: string;
  strength: string;
  dosageform: string;
  disposalquantity: number;
  disposalreasontext: string;
  disposalmethodtext: string;
  disposallocationtext: string;
  disposaldate: string;
  status: string;
  isverified: boolean;
  total_count: number;
  pending_verification?: number;
  completed_verification?: number;
}

const DisposalList: React.FC = () => {
  document.title = "Drug Destruction & Disposal | ZENTIXS ORBIT";
  const navigate = useNavigate();
  const userData = GetUserData();
  const [searchText, setSearchText] = useState<string>("");
  const [currentPageNumber, setcurrentPageNumber] = useState<number>(1);

  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [verifyModal, setVerifyModal] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<DisposalRecord | null>(
    null,
  );
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);

  //Audit
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();

  const { selectedStudy: globalStudy } = useGlobalStudy();

  const onAuditClick = () => {
    const payload = {
      modulename: "Disposal",
      studyid: globalStudy?.studyid
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Disposal_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  const { SelectAllQuery, DeleteMutation, verifyPinMutation } = useDisposalService({
    searchText,
    pageNumber: currentPageNumber,
    pageSize: 5,
    studyid: globalStudy?.studyid ?? null,
    enableSelectall: true,
  });

  const queryResponse = SelectAllQuery.data as any;
  const data: DisposalRecord[] = queryResponse?.data ?? [];

  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pendingVerification, setPendingVerification] = useState<number>(0);
  const [completedVerification, setCompletedVerification] = useState<number>(0);

  useEffect(() => {
    if (queryResponse) {
      setTotalRecords(data[0]?.total_count || 0);
      setPendingVerification(data[0]?.pending_verification || 0);
      setCompletedVerification(data[0]?.completed_verification || 0);
    }
  }, [queryResponse]);

  const onSearch = (value: string | null) => {
    setSearchText(value ?? "");
    setcurrentPageNumber(1);
  };

  const handleDeleteClick = (item: DisposalRecord) => {
    setSelectedRecord(item);
    setDeleteModal(true);
  };

  const handleDeleteWithReason = (payload: {
    id: number | null;
    reason: string;
  }) => {
    if (!selectedRecord) return;
    DeleteMutation.mutate(
      { disposalid: selectedRecord.disposalid, updatereason: payload.reason },
      {
        onSuccess: (response: any) => {
          showToastMessage("success", response.message);
          queryClient.invalidateQueries({ queryKey: ["disposal-selectAll"] });
          queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
          setDeleteModal(false);
          setSelectedRecord(null);
        },
        onError: (error: any) => {
          showToastMessage("error", error?.message ?? error);
        },
      },
    );
  };

  const handleVerifyClick = (item: DisposalRecord) => {
    setSelectedRecord(item);
    setVerifyModal(true);
  };

  const handlePinVerify = (password: string) => {
    const payload = {
      verifypin: password,
      disposalid: Number(selectedRecord?.disposalid),
      studyid: Number(selectedRecord?.studyid),
      verifiedby: Number(userData?.userid),
    };

    verifyPinMutation.mutate(payload, {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries({ queryKey: ["disposal-selectAll"] });
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
        showToastMessage("success", response?.message);
        setVerifyModal(false);
        setSelectedRecord(null);
      },
      onError: (error: Error) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  const columns: any = [
    {
      key: "disposalnumber",
      label: "AUTH ID",
      type: "link",
      onClick: (row: any) =>
        navigate(DISPOSAL_PATHS.VIEW.replace(":id", row.disposalid.toString())),
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.link}>{row.disposalnumber || "-"}</p>
          </>
        );
      },
    },
    {
      key: "studycode",
      label: "STUDY NO",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.studycode || "-"}</p>
            <p className={ip.cellSecondary}>{row.ipcategory || "-"}</p>
          </>
        );
      },
    },
    {
      key: "productname",
      label: "PRODUCT DETAILS",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.productname || "-"}</p>
            <p className={ip.cellSecondary}>
              {row.batchlotno || "-"} | {row.strength || "-"} |{" "}
              {row.dosageform || ""}
            </p>
          </>
        );
      },
    },
    {
      key: "disposalquantity",
      label: "QUANTITY",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>
              {row.disposalquantity || "0"} Out of{" "}
              {row.totalquantity || "0"}{" "}
            </p>
            <p className={ip.cellSecondary}>For Disposal</p>
          </>
        );
      },
    },
    {
      key: "execution",
      label: "EXECUTION",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.disposalmethodtext || "-"}</p>
            <p className={ip.cellSecondary}>
              {row.disposallocationtext || "-"}
            </p>
          </>
        );
      },
    },
    {
      key: "disposaldate",
      label: "DISPOSAL DATE",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>
              {formatDate(row.disposaldate) || "-"}
            </p>
            <p className={ip.cellSecondary}>
              Reason: {row.disposalreasontext || "-"}
            </p>
          </>
        );
      },
    },
    {
      key: "verifiedby",
      label: "Verified By",
      render: (_: any, row?: any) => {
        if (!row) return null;
        const label = row.verifiedby ? row.verifiedbyname : "-";
        return (
          <>
            <p className={ip.cellPrimary}>{label}</p>
            <p className={ip.cellSecondary}>
              {formatDate(row.verifieddate) || "-"}
            </p>
          </>
        );
      },
    },
  ];

  const getRowActions = (row: DisposalRecord) => ({
    onEdit: !row.isverified
      ? (r: any) =>
        navigate(DISPOSAL_PATHS.EDIT.replace(":id", r.disposalid.toString()))
      : undefined,
    onDelete: !row.isverified ? (r: any) => handleDeleteClick(r) : undefined,
    onVerify: !row.isverified ? (r: any) => handleVerifyClick(r) : undefined,
  });

  return (
    <div className={styles.dashboardContainer}>
      <Container fluid>
        {!globalStudy?.studyid ? (
          <div >
            <NoStudySelectedPlaceholder moduleName="disposal requests" />
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mt-4 mb-3 px-2">
              <div>
                <h5 className={`mb-0 ${styles.pageHeaderTitle}`}>
                  Drug Destruction & Disposal
                </h5>
                <p className={styles.pageHeaderSubtitle}>
                  Manage and review disposal protocols for clinical trial materials.
                </p>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <CommonButton
                  module={PERMISSION_MODULES.DRUG_DISPOSAL}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                  className="fw-600"
                  size="sm"
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
                <CommonButton
                  color="primary"
                  className="px-2 fw-600"
                  outline
                  module={PERMISSION_MODULES.DRUG_DISPOSAL}
                  action={PERMISSION_ACTIONS.ADD}
                  onClick={() => navigate(DISPOSAL_PATHS.ADD)}
                  size="sm"
                >
                  <i className="ri-add-line me-1" /> New Disposal Request
                </CommonButton>
              </div>
            </div>
            <Row className="g-3 mb-3 px-1">
              <Col md={4}>
                <StatCard
                  title="TOTAL DISPOSALS"
                  value={totalRecords}
                  subtext="Disposal records tracked"
                  icon={<i className="ri-delete-bin-6-line" />}
                  bgColor="#eff6ff"
                  iconColor="#2563eb"
                  trendType="info"
                  subtextIcon="ri-stack-line"
                />
              </Col>

              <Col md={4}>
                <StatCard
                  title="PENDING VERIFICATION"
                  value={pendingVerification}
                  subtext="Requires verification"
                  icon={<i className="ri-time-line" />}
                  bgColor="#fffbeb"
                  iconColor="#d97706"
                  trendType="negative"
                />
              </Col>

              <Col md={4}>
                <StatCard
                  title="COMPLETED VERIFICATION"
                  value={completedVerification}
                  subtext="Verified disposal records"
                  icon={<i className="ri-verified-badge-line" />}
                  bgColor="#ecfdf5"
                  iconColor="#059669"
                  trendType="success"
                  subtextIcon="ri-check-double-line"
                />
              </Col>
            </Row>

            <div className={styles.listPanel}>
              <div className={styles.panelHeader}>
                <h2>
                  <i className="ri-flask-line me-2 text-primary fs-18" />
                  Disposal Records
                </h2>
                <CommonSearch
                  onSearch={onSearch}
                  searchText={searchText}
                  placeholder="Search..."
                />
              </div>

              <div className="px-4">
                <CommonTableIndexWise
                  moduleName={PERMISSION_MODULES.DRUG_DISPOSAL}
                  columns={columns}
                  data={data}
                  loading={SelectAllQuery.isLoading || SelectAllQuery.isFetching}
                  pageSize={5}
                  currentPage={currentPageNumber}
                  totalRecords={data[0]?.total_count || 0}
                  onPageChange={setcurrentPageNumber}
                  actions={getRowActions}
                />
              </div>
            </div>
          </>
        )}

        {/* File Preview Modal */}
        {isPreviewOpen && (
          <FilePreviewModal
            file={previewFile}
            isOpen={isPreviewOpen}
            size={"xl"}
            onClose={() => {
              setIsPreviewOpen(false);
              setPreviewFile(null);
            }}
          />
        )}

        {deleteModal && (
          <DeleteModal
            isopen={deleteModal}
            recordId={selectedRecord?.disposalid}
            recordName={`${selectedRecord?.disposalnumber}`}
            toggle={() => setDeleteModal(false)}
            onDeleteClick={handleDeleteWithReason}
            isLoading={DeleteMutation.isPending}
            showDeleteReason={showDeleteReason}
            toggleDeleteReason={() => setShowDeleteReason(!showDeleteReason)}
          />
        )}

        <PinVerificationModal
          isOpen={verifyModal}
          toggle={() => setVerifyModal(false)}
          onVerify={handlePinVerify}
          isLoading={verifyPinMutation.isPending}
          title="Verify Disposal Record"
          description={
            <>
              Please enter your password to verify Record: <strong>{`${selectedRecord?.disposalnumber}`}</strong>
            </>
          }
          isInvalid={verifyPinMutation.isError}
        />
      </Container>
    </div>
  );
};

export default DisposalList;
